// CRM statuses v2 (2026-10-16): the stage model from GET /crm/stages, used
// ONLY when the server says STATUS_NAMES_V2 covers this login (/auth/me ->
// status_names_v2). With the switch off nothing here runs: CRMQueue keeps its
// own constants and every label and tab is exactly as before.
//
// Style tokens map to FULL Tailwind class strings written out below, so the
// build keeps them (a class name that only arrives from the server is never
// generated into the CSS).
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../api/client";
import { setStageColorOverrides } from "../constants/stageColors";

export const STYLE_BY_TOKEN = {
  "emerald":      { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", hex: "#10b981" },
  "blue":         { bg: "bg-blue-100",    text: "text-blue-800",    dot: "bg-blue-500",    hex: "#3b82f6" },
  "indigo":       { bg: "bg-indigo-100",  text: "text-indigo-800",  dot: "bg-indigo-500",  hex: "#6366f1" },
  "amber":        { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500",   hex: "#f59e0b" },
  "purple":       { bg: "bg-purple-100",  text: "text-purple-800",  dot: "bg-purple-500",  hex: "#a855f7" },
  "orange":       { bg: "bg-orange-100",  text: "text-orange-800",  dot: "bg-orange-500",  hex: "#f97316" },
  "orange-light": { bg: "bg-orange-100",  text: "text-orange-800",  dot: "bg-orange-400",  hex: "#fb923c" },
  "slate":        { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400",   hex: "#94a3b8" },
  "sky":          { bg: "bg-sky-100",     text: "text-sky-800",     dot: "bg-sky-500",     hex: "#0ea5e9" },
  "teal":         { bg: "bg-teal-100",    text: "text-teal-800",    dot: "bg-teal-500",    hex: "#14b8a6" },
  "emerald-dark": { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-600", hex: "#059669" },
  "fuchsia":      { bg: "bg-fuchsia-100", text: "text-fuchsia-800", dot: "bg-fuchsia-500", hex: "#d946ef" },
  "rose":         { bg: "bg-rose-100",    text: "text-rose-800",    dot: "bg-rose-500",    hex: "#f43f5e" },
  "violet":       { bg: "bg-violet-100",  text: "text-violet-800",  dot: "bg-violet-500",  hex: "#8b5cf6" },
  "red":          { bg: "bg-red-100",     text: "text-red-800",     dot: "bg-red-500",     hex: "#ef4444" },
};
const FALLBACK_STYLE = STYLE_BY_TOKEN.blue;
export const GROUP_ORDER = ["Contact", "Scheduled", "Treating", "Closed"];

// Build the v2 model from the /crm/stages payload.
export function buildStageModel(payload) {
  const list = (payload || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const byId = {};
  list.forEach((s) => { byId[s.stage_id] = s; });
  const groups = GROUP_ORDER.map((key) => {
    const stages = list.filter((s) => s.group === key);
    const subs = [];
    stages.forEach((s) => {
      const label = s.sub_label || null;
      let sub = subs.find((x) => x.label === label);
      if (!sub) { sub = { label, stages: [] }; subs.push(sub); }
      sub.stages.push(s);
    });
    return { key, label: (stages[0] && stages[0].group_label) || key, stages, subs };
  });
  const closedIds = list.filter((s) => s.category === "Closed").map((s) => s.stage_id);
  return {
    v2: true,
    list,
    byId,
    groups,
    closedIds,
    styleFor: (id) => STYLE_BY_TOKEN[(byId[id] || {}).ui] || FALLBACK_STYLE,
    labelFor: (id, fallback) => (byId[id] && byId[id].display_v2) || fallback || "",
    isClosed: (id) => closedIds.includes(id),
    isAvailable: (id) => !!(byId[id] && byId[id].available),
  };
}

let cache = null; // one fetch per page load: { v2, payload }
function load() {
  if (!cache) {
    cache = api.get("/auth/me")
      .then((r) => (r.data && r.data.status_names_v2
        ? api.get("/crm/stages").then((s) => ({ v2: true, payload: s.data || [] }))
        : { v2: false, payload: null }))
      .catch(() => ({ v2: false, payload: null })); // any failure = today's screens
  }
  return cache;
}

// null while loading and whenever the switch is off.
export function useStagesV2() {
  const [model, setModel] = useState(null);
  useEffect(() => {
    let live = true;
    load().then(({ v2, payload }) => {
      if (!live || !v2 || !payload) return;
      const m = buildStageModel(payload);
      const hex = {};
      m.list.forEach((s) => { hex[s.stage_id] = (STYLE_BY_TOKEN[s.ui] || FALLBACK_STYLE).hex; });
      setStageColorOverrides(hex);
      setModel(m);
    });
    return () => { live = false; };
  }, []);
  return model;
}

export const StageModelContext = createContext(null);
export function useStageModel() {
  return useContext(StageModelContext);
}
